import React, { useState, useEffect } from "react";
import {
  AppBar,
  Typography,
  Button,
  TextField,
  Box,
  Breadcrumbs,
  Autocomplete,
  InputAdornment,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  IconButton,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useNavigate } from "react-router-dom";
import { useStyles } from "../../assets/styles/Theme.tsx";
import { useTheme } from "@mui/material/styles";
import {
  externalgetApi,
  externalpostApi,
} from "../../services/externalApis.tsx";
import { SETTING_API_20, SETTING_API_02 } from "../../api/ApiModel.tsx";
import ViewDataGrid from "../../components/grid/ViewDataGrid.tsx";
import BorderColorOutlinedIcon from "@mui/icons-material/BorderColorOutlined";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import {
  Search as SearchIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { usePageData } from "../../utils/common/PageDataContext.tsx";
import { CON_STATUS_ACTIVE, CON_STATUS_DELETED } from "../../utils/sys/Constants.tsx";
import { useSelector } from "react-redux";
import SystemAlert from "../../components/common/SystemAlert.tsx";
import { MainCatergorydata } from "../../components/grid/GridData.tsx";
// Import messages
import {
  Messages,
  MessageCategory,
} from "../../utils/sys/Messages.tsx";

// Types and Interfaces
interface MainCategory {
  maincatcode?: string | number;
  maincatname?: string;
  descrip?: string;
  sts?: string;
  enuser?: string;
  endate?: string;
}

interface MainCategoryMapped {
  catcode: string | number;
  catname: string;
  descrip?: string;
  sts?: string;
  enuser?: string;
  endate?: string;
  [key: string]: unknown;
}

interface Category {
  catcode: number;
  catname: string;
  maincatcode: number;
  [key: string]: any;
}

interface UserParams {
  LOCCODE?: string;
  STAFFMAPLOCODE?: string;
  COMPCODE?: string;
  USERNAME?: string;
}

interface UserState {
  USER_PARAM?: UserParams;
}

interface MenuItem {
  text: string;
  icon: React.ReactElement;
  onClick: (id: string | number) => void;
}

interface ApiError {
  response?: {
    status?: number;
    data?: string | { message?: string; error?: string };
  };
  request?: unknown;
  message?: string;
}

interface DeletePayload {
  COMPCODE?: string;
  LOCCODE?: string;
  maincatcode: string | number;
  maincatname: string;
  descrip: string;
  sts: string;
  enuser: string;
  MODE: string;
}

interface ApiResponse {
  success?: boolean;
  message?: string;
}

interface PageData {
  title: string;
  [key: string]: unknown;
}

interface RowWithId extends MainCategoryMapped {
  id: string | number;
}

function ViewMainCategory(): React.ReactElement {
  const theme = useTheme();
  const styles = useStyles(theme);
  const navigate = useNavigate();
  const { setPageData } = usePageData();

  const SYS_USER = useSelector((state: { user: UserState }) => state.user) || {};
  const { LOCCODE, STAFFMAPLOCODE, COMPCODE, USERNAME } =
    SYS_USER.USER_PARAM || {};

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isCheckingUsage, setIsCheckingUsage] = useState<boolean>(false);
  const [data, setData] = useState<MainCategoryMapped[]>([]);
  const [filteredRows, setFilteredRows] = useState<MainCategoryMapped[]>([]);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [alertOpen, setAlertOpen] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<MessageCategory.SUCCESS | MessageCategory.ERROR | MessageCategory.WARNING>(MessageCategory.SUCCESS);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);
  const [selectedId, setSelectedId] = useState<string | number | null>(null);
  const [selectedItem, setSelectedItem] = useState<MainCategoryMapped | null>(null);

  // ------------ action menu ------------
  const menuItems: MenuItem[] = [
    {
      text: Messages.mainCategory.action.edit,
      icon: <BorderColorOutlinedIcon />,
      onClick: (id: string | number) => handleItemClick(id, Messages.mainCategory.action.edit),
    },
    {
      text: Messages.mainCategory.action.delete,
      icon: <DeleteIcon />,
      onClick: (id: string | number) => handleItemClick(id, Messages.mainCategory.action.delete),
    },
  ];

  // ------------ Fetch main categories ------------
  const fetchDataMainCatedory = async (): Promise<void> => {
    setIsLoading(true);
    try {
      const result = (await externalgetApi(SETTING_API_20, "get", {
        COMPCODE,
        LOCCODE: STAFFMAPLOCODE,
        STS: CON_STATUS_ACTIVE,
      })) as MainCategory[];

      const mappedData: MainCategoryMapped[] = result.map((item) => ({
        catcode: item.maincatcode || "",
        catname: item.maincatname || "",
        descrip: item.descrip,
        sts: item.sts,
        enuser: item.enuser,
        endate: item.endate,
      }));

      setData(mappedData);
      setFilteredRows(mappedData);
    } catch (err) {
      console.error(err);
      setAlertMessage(Messages.mainCategory.error.fetchFailed);
      setAlertType(MessageCategory.ERROR);
      setAlertOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  // Function to check if main category is used in any categories/subcategories
  const checkMainCategoryUsage = async (maincatcode: string | number): Promise<boolean> => {
    try {
      setIsCheckingUsage(true);
      
      // Fetch categories with the same main category code
      const categories = await externalgetApi(SETTING_API_02, "get", {
        COMPCODE,
        LOCCODE: STAFFMAPLOCODE,
        MAINCATCODE: maincatcode,
        STS: CON_STATUS_ACTIVE,
      });

      // Check if any categories use this main category
      const isUsed = categories?.length > 0;

      return isUsed;
    } catch (error) {
      console.error("Error checking main category usage:", error);
      return false;
    } finally {
      setIsCheckingUsage(false);
    }
  };

  //-------------- Search-------------
  const handleSearchChange = (event: React.SyntheticEvent, value: string | null): void => {
    const searchValue = value || "";
    setSearchQuery(searchValue);
    
    if (!searchValue.trim()) {
      setFilteredRows(data || []);
      return;
    }

    const q = searchValue.toLowerCase().trim();
    
    const filtered = data?.filter((row) => {
      const catcodeStr = row.catcode?.toString() || "";
      
      return (
        row.catname?.toLowerCase().includes(q) ||
        catcodeStr.toLowerCase().includes(q)
      );
    });
    
    setFilteredRows(filtered || []);
  };

  //------------ Handle navigation for Edit/Delete ------------
  const handleItemClick = (id: string | number, actionText: string): void => {
    const item = data.find((row) => row.catcode === id);
    if (!item) return;

    if (actionText === Messages.mainCategory.action.edit) {
      navigate("/createMainCategory", {
        state: {
          selectedItem: {
            ...item,
            maincatcode: item.catcode,
          },
          actionText,
        },
      });
    }

    if (actionText === Messages.mainCategory.action.delete) {
      setSelectedId(id);
      setSelectedItem(item);
      setOpenDeleteDialog(true); // Open confirmation dialog immediately
    }
  };

  const addToView = (): void => {
    navigate("/createMainCategory");
  };

  // ------------ Confirm Delete ------------
  const confirmDelete = async (): Promise<void> => {
    try {
      setIsLoading(true);

      if (!selectedId || !selectedItem) {
        throw new Error(Messages.mainCategory.warning.noCategorySelected);
      }

      // Check if main category is used in any categories
      const isUsed = await checkMainCategoryUsage(selectedId);
      
      // If main category is in use, show error message and don't delete
      if (isUsed) {
        setAlertMessage("Main Category cannot be deleted. It's already in use");
        setAlertType(MessageCategory.ERROR);
        setAlertOpen(true);
        setOpenDeleteDialog(false);
        setSelectedId(null);
        setSelectedItem(null);
        return;
      }

      // Validate required parameters
      if (!COMPCODE) {
        throw new Error(Messages.mainCategory.error.compCodeMissing);
      }
      if (!STAFFMAPLOCODE) {
        throw new Error(Messages.mainCategory.error.locCodeMissing);
      }
      if (!USERNAME) {
        throw new Error(Messages.mainCategory.error.usernameMissing);
      }

      const deletePayload: DeletePayload = {
        COMPCODE: COMPCODE || "",
        LOCCODE: STAFFMAPLOCODE || "",
        maincatcode: selectedId,
        maincatname: selectedItem.catname || "",
        descrip: selectedItem.descrip || "",
        sts: CON_STATUS_DELETED || "D",
        enuser: USERNAME || "Admin",
        MODE: "DELETE",
      };

      console.log("DELETE payload:", deletePayload);
      console.log("DELETE URL:", SETTING_API_20);

      const response = (await externalpostApi(
        SETTING_API_20,
        deletePayload
      )) as ApiResponse;
      
      console.log("DELETE response:", response);
      
      if (response && response.success !== false) {
        // Remove from local state
        setData((prev) => prev.filter((row) => row.catcode !== selectedId));
        setFilteredRows((prev) =>
          prev.filter((row) => row.catcode !== selectedId)
        );

        // Show success message
        const successMessage = Messages.mainCategory.success.delete(selectedItem.catname || "item");
        setAlertMessage(successMessage);
        setAlertType(MessageCategory.SUCCESS);
        setAlertOpen(true);
        setOpenDeleteDialog(false);
        setSelectedId(null);
        setSelectedItem(null);
      } else {
        throw new Error(response?.message || Messages.mainCategory.error.deleteFailed);
      }
    } catch (error) {
      const err = error as ApiError;
      console.error("DELETE API error:", err);

      // Error handling
      let errorMessage = Messages.mainCategory.error.deleteFailed;

      if (err.response) {
        console.error("Response Status:", err.response.status);
        console.error("Response Data:", err.response.data);

        if (err.response.data) {
          if (typeof err.response.data === "string") {
            if (err.response.data.includes("Object reference not set to an instance of an object")) {
              errorMessage = Messages.measuringUnit?.error?.nullReference;
            } else if (err.response.data.trim() !== "") {
              errorMessage = err.response.data;
            }
          } else if ((err.response.data as any).message) {
            errorMessage = (err.response.data as any).message;
          } else if ((err.response.data as any).error) {
            errorMessage = (err.response.data as any).error;
          }
        }

        // Handle specific HTTP status codes
        switch (err.response.status) {
          case 400:
            errorMessage = errorMessage || Messages.measuringUnit?.error?.badRequest;
            break;
          case 404:
            errorMessage = Messages.measuringUnit?.error?.notFound;
            break;
          case 405:
            errorMessage = Messages.measuringUnit?.error?.methodNotAllowed;
            break;
          case 500:
            errorMessage = errorMessage || Messages.measuringUnit?.error?.serverError;
            break;
          default:
            errorMessage = `${errorMessage || Messages.mainCategory.error.deleteFailed} (Status: ${err.response.status})`;
        }
      } else if (err.request) {
        errorMessage = Messages.measuringUnit?.error?.noResponse;
      } else {
        errorMessage = err.message || errorMessage;
      }

      setAlertMessage(errorMessage);
      setAlertType(MessageCategory.ERROR);
      setAlertOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  const cancelDelete = (): void => {
    setOpenDeleteDialog(false);
    setSelectedId(null);
    setSelectedItem(null);
  };

  // ------------- useEffect ----------
  useEffect(() => {
    setPageData({ title: Messages.mainCategory.pageTitle.view } as PageData);
  }, [setPageData]);

  useEffect(() => {
    fetchDataMainCatedory();
  }, [COMPCODE, STAFFMAPLOCODE]);

  // Prepare rows with id for the grid
  const rowsWithId: RowWithId[] = filteredRows.map((row) => ({
    ...row,
    id: row.catcode || Math.random(),
  }));

  // Placeholder processRowUpdate function
  const processRowUpdate = async (newRow: any): Promise<any> => {
    console.log("Row update attempted:", newRow);
    return newRow;
  };

  return (
    <>
      <Box sx={styles.box_breadcrumb_view}>
        <Breadcrumbs
          separator="›"
          aria-label="breadcrumb"
          sx={styles.breadcrumb}
        >
          <Link underline="hover" sx={styles.breadcrumb_link}>
            {Messages.ui.breadcrumb.home}
          </Link>
          <Link underline="hover" sx={styles.breadcrumb_link}>
            {Messages.ui.breadcrumb.settings}
          </Link>
          <Link
            underline="hover"
            sx={styles.breadcrumb_link_page}
            aria-current="page"
          >
            {Messages.mainCategory.breadcrumb.view}
          </Link>
        </Breadcrumbs>
      </Box>

      <Box sx={styles.box_page_container}>
        <AppBar sx={styles.appbar}>
          <Grid container spacing={2} sx={styles.appbar_grid_container}>
            <Grid
              size={{ xs: 12, sm: 12, md: 6, lg: 8, xl: 8 }}
              sx={styles.appbar_grid_container_col_1}
            >
              <Typography sx={styles.appbar_title}>
                {MainCatergorydata.name || Messages.mainCategory.tableTitle}
              </Typography>
            </Grid>
            <Grid
              size={{ xs: 12, sm: 12, md: 6, lg: 4, xl: 4 }}
              sx={styles.appbar_grid_search}
            >
              <Autocomplete
                freeSolo
                id="act_appbarsearch"
                fullWidth
                disableClearable
                value={searchQuery}
                onInputChange={handleSearchChange}
                sx={{ ...styles.ipt_appbar_search_auto, minWidth: 200 }}
                options={data?.map((row) => row.catname) || []}
                renderInput={(params) => {
                  const { id, ...restParams } = params;
                  return (
                    <TextField
                      id="txt_appbarsearch"
                      {...restParams}
                      placeholder={Messages.mainCategory.placeholder.search}
                      size="small"
                      sx={styles.ipt_appbar_search}
                      InputProps={{
                        ...restParams.InputProps,
                        type: "search",
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              id="btn_appbarsearch"
                              sx={styles.btn_appbar_search_icon}
                            >
                              <SearchIcon id="ico_search" />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  );
                }}
              />
              <Button
                id="btn_add"
                variant="contained"
                startIcon={<AddCircleOutlineIcon />}
                size="small"
                sx={{ ...styles.btn_primary, ...styles.btn_appbar_add }}
                onClick={addToView}
                disabled={isLoading}
              >
                {Messages.ui.button.add}
              </Button>
            </Grid>
          </Grid>
        </AppBar>

        {/* Data Grid View */}
        <Box sx={styles.view_page_container}>
          <ViewDataGrid
            columns={MainCatergorydata.columns}
            rows={rowsWithId}
            menuItems={menuItems}
            tabletitle={MainCatergorydata.name || Messages.mainCategory.tableTitle}
            gridid="catcode"
            processRowUpdate={processRowUpdate}
          />
        </Box>
      </Box>

      <SystemAlert
        open={alertOpen}
        type={alertType}
        message={alertMessage}
        onClose={() => setAlertOpen(false)}
      />

      <Dialog
        id="dlg_delete"
        open={openDeleteDialog}
        onClose={cancelDelete}
        aria-labelledby="delete-dialog-title"
      >
        <DialogTitle id="delete-dialog-title">{Messages.mainCategory.dialog.deleteTitle}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {selectedItem ? (
              Messages.mainCategory.dialog.deleteConfirmationNamed(selectedItem.catname?.toString() || "item")
            ) : (
              Messages.mainCategory.dialog.deleteConfirmation
            )}
          </DialogContentText>
          {/* {selectedItem?.descrip && (
            <DialogContentText sx={{ mt: 2, fontStyle: 'italic' }}>
              Description: {selectedItem.descrip}
            </DialogContentText>
          )} */}
        </DialogContent>
        <DialogActions>
          <Button
            id="btn_cancel"
            onClick={cancelDelete}
            variant="outlined"
            sx={{ ...styles.btn_secondary, ...styles.btn_add_cancel }}
            disabled={isLoading || isCheckingUsage}
          >
            {Messages.ui.button.cancel}
          </Button>
          <Button
            id="btn_confirm_delete"
            onClick={confirmDelete}
            color="error"
            sx={{ ...styles.btn_primary, ...styles.btn_add_confirm }}
            variant="contained"
            disabled={isLoading || isCheckingUsage}
          >
            {isLoading ? Messages.mainCategory.button.deleting : Messages.ui.button.delete}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default ViewMainCategory;